/**
 * TanStack Query hooks for the in-house error tracker admin surface.
 * See docs/architecture/error-tracking-plan.md.
 *
 * The response envelope from the backend is { data, meta } for lists
 * and { data } for single-row / count endpoints. Hooks unwrap the data
 * key so callers get a stable shape; envelope details stay contained
 * here.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@services/http';

// ─────────────────────────────────────────────────────────────────────
// Query key factory
// ─────────────────────────────────────────────────────────────────────
export const errorLogKeys = {
  all: ['errorLogs'],
  list: (params) => ['errorLogs', 'list', params],
  detail: (id) => ['errorLogs', 'detail', String(id ?? '')],
  unresolvedCount: ['errorLogs', 'unresolvedCount'],
};

// ─────────────────────────────────────────────────────────────────────
// Low-level fetchers
// ─────────────────────────────────────────────────────────────────────
async function fetchList(params, { signal } = {}) {
  const { data } = await http.get('/api/admin/errors', { params, signal });
  return data;
}

async function fetchDetail(id, { signal } = {}) {
  const { data } = await http.get(`/api/admin/errors/${id}`, { signal });
  return data?.data ?? null;
}

async function fetchUnresolvedCount({ signal } = {}) {
  const { data } = await http.get('/api/admin/errors/unresolved-count', { signal });
  return data?.count ?? 0;
}

// ─────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────

/**
 * Paginated + filtered error list. `params` should include:
 *   { page, per_page, environment, resolved }
 * The list order is server-controlled (last_seen DESC by default).
 */
export function useErrorLogs(params, options = {}) {
  return useQuery({
    queryKey: errorLogKeys.list(params),
    queryFn: ({ signal }) => fetchList(params, { signal }),
    ...options,
  });
}

/**
 * Single-row detail. Returns null if id is falsy (so the hook can be
 * unconditionally rendered but only fires when we have a real id).
 */
export function useErrorLog(id, options = {}) {
  return useQuery({
    queryKey: errorLogKeys.detail(id),
    queryFn: ({ signal }) => fetchDetail(id, { signal }),
    enabled: Boolean(id) && (options.enabled ?? true),
    ...options,
  });
}

/**
 * Count of unresolved errors seen in the last 24h. Powers the nav badge.
 * Polled every 60s so admins get a live-ish signal without hammering
 * the endpoint.
 */
export function useUnresolvedErrorsCount(options = {}) {
  return useQuery({
    queryKey: errorLogKeys.unresolvedCount,
    queryFn: ({ signal }) => fetchUnresolvedCount({ signal }),
    refetchInterval: 60_000,
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Mutations for resolve/reopen. Invalidate both the list and the
 * specific detail so anything currently on-screen reflects the new
 * state, and refetch the badge count so the nav pill updates too.
 */
export function useResolveErrorLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await http.post(`/api/admin/errors/${id}/resolve`);
      return data?.data ?? null;
    },
    onSuccess: (row) => {
      if (row?.id) {
        qc.setQueryData(errorLogKeys.detail(row.id), row);
      }
      qc.invalidateQueries({ queryKey: errorLogKeys.all });
    },
  });
}

export function useReopenErrorLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await http.post(`/api/admin/errors/${id}/reopen`);
      return data?.data ?? null;
    },
    onSuccess: (row) => {
      if (row?.id) {
        qc.setQueryData(errorLogKeys.detail(row.id), row);
      }
      qc.invalidateQueries({ queryKey: errorLogKeys.all });
    },
  });
}
